<template>
  <v-card>
    <v-card-title class="d-flex justify-space-between align-center">
      <span>Schools Management</span>
      <div class="d-flex ga-2">
        <v-btn color="secondary" variant="tonal" @click="openBulkAddModal">
          <v-icon start>mdi-file-upload</v-icon>
          Bulk Add Schools
        </v-btn>
        <v-btn color="primary" @click="openAddModal">
          <v-icon start>mdi-plus</v-icon>
          Add School
        </v-btn>
      </div>
    </v-card-title>

    <v-card-text>
      <v-text-field
        v-model="searchQuery"
        label="Search schools..."
        prepend-inner-icon="mdi-magnify"
        variant="outlined"
        density="compact"
        clearable
        class="mb-4"
        hide-details
      ></v-text-field>

      <v-data-table
        :headers="headers"
        :items="filteredSchools"
        :loading="loading"
        :items-per-page="10"
        class="elevation-1"
      >
        <!-- County Column -->
        <template v-slot:item.county="{ item }">
          <v-chip color="secondary" size="small">
            {{ (item as any).county }}
          </v-chip>
        </template>

        <!-- POC Column -->
        <template v-slot:item.poc="{ item }">
          <span v-if="(item as any).poc">
            {{ getUserName((item as any).poc) }}
          </span>
          <span v-else class="text-grey">-</span>
        </template>

        <!-- Actions Column -->
        <template v-slot:item.actions="{ item }">
          <v-btn
            icon="mdi-pencil"
            size="small"
            variant="text"
            @click="openEditModal(item)"
          ></v-btn>
          <v-btn
            icon="mdi-delete"
            size="small"
            variant="text"
            color="error"
            @click="deleteSchool(item)"
          ></v-btn>
        </template>
      </v-data-table>
    </v-card-text>

    <!-- Add/Edit School Modal -->
    <v-dialog v-model="showModal" max-width="600px">
      <v-card>
        <v-card-title>
          <span class="text-h5">{{ isEditing ? 'Edit School' : 'Add School' }}</span>
        </v-card-title>

        <v-card-text>
          <v-form ref="form">
            <v-text-field
              v-model="formData.name"
              label="School Name"
              variant="outlined"
              :rules="[v => !!v || 'School name is required']"
              required
            ></v-text-field>

            <v-text-field
              v-model="formData.county"
              label="County"
              variant="outlined"
              :rules="[v => !!v || 'County is required']"
              required
            ></v-text-field>

            <v-select
              v-model="formData.poc"
              :items="userOptions"
              label="Point of Contact (Optional)"
              variant="outlined"
              clearable
            ></v-select>
          </v-form>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="grey" @click="closeModal">Cancel</v-btn>
          <v-btn color="primary" @click="saveSchool" :loading="submitting">
            {{ isEditing ? 'Update' : 'Create' }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Bulk Add Schools Modal -->
    <v-dialog v-model="showBulkModal" max-width="700px">
      <v-card>
        <v-card-title>
          <span class="text-h5">Bulk Add Schools</span>
        </v-card-title>

        <v-card-text>
          <v-form ref="bulkForm">
            <v-text-field
              v-model="bulkFormData.county"
              label="County"
              variant="outlined"
              :rules="[v => !!v || 'County is required']"
              required
              class="mb-4"
              hint="All schools in the list will be assigned to this county"
              persistent-hint
            ></v-text-field>

            <v-textarea
              v-model="bulkFormData.schoolsList"
              label="Schools List"
              variant="outlined"
              :rules="[v => !!v || 'Schools list is required']"
              required
              rows="10"
              hint="Enter one school name per line"
              persistent-hint
              placeholder="Example:&#10;School Name 1&#10;School Name 2&#10;School Name 3"
            ></v-textarea>

            <v-alert v-if="bulkPreview.length > 0" type="info" variant="tonal" class="mt-4">
              <div class="text-subtitle-2 mb-2">Preview: {{ bulkPreview.length }} school(s) will be created</div>
              <div class="text-caption">
                <div v-for="(school, index) in bulkPreview.slice(0, 5)" :key="index">
                  • {{ school }}
                </div>
                <div v-if="bulkPreview.length > 5" class="text-grey mt-1">
                  ... and {{ bulkPreview.length - 5 }} more
                </div>
              </div>
            </v-alert>
          </v-form>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="grey" @click="closeBulkModal">Cancel</v-btn>
          <v-btn color="primary" @click="bulkCreateSchools" :loading="submittingBulk">
            Create {{ bulkPreview.length }} School(s)
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-card>
</template>

<script setup lang="ts">
import { ref, onMounted, computed } from 'vue'
import { api } from '@/composables/api'
import type { School } from '@/types/School'

const schools = ref<School[]>([])
const loading = ref(false)
const showModal = ref(false)
const isEditing = ref(false)
const editingId = ref<string | null>(null)
const submitting = ref(false)
const users = ref<any[]>([])
const searchQuery = ref('')

// Bulk add state
const showBulkModal = ref(false)
const submittingBulk = ref(false)
const bulkFormData = ref({
  county: '',
  schoolsList: ''
})

const headers = [
  { title: 'Name', key: 'name', sortable: true },
  { title: 'County', key: 'county', sortable: true },
  { title: 'Point of Contact', key: 'poc', sortable: false },
  { title: 'Actions', key: 'actions', sortable: false }
]

const formData = ref({
  name: '',
  county: '',
  poc: null as string | null
})

const userOptions = computed(() => {
  return users.value.map(user => ({
    title: `${user.first_name || ''} ${user.last_name || ''} (${user.email})`.trim(),
    value: user.id
  }))
})

const getUserName = (userId: string) => {
  const user = users.value.find(u => u.id === userId)
  if (!user) return 'Unknown'
  return `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.email
}

// Filtered schools based on search query
const filteredSchools = computed(() => {
  if (!searchQuery.value) return schools.value

  const query = searchQuery.value.toLowerCase()
  return schools.value.filter(school =>
    school.name.toLowerCase().includes(query) ||
    (school.county && school.county.toLowerCase().includes(query))
  )
})

// Bulk add preview - parse the schools list
const bulkPreview = computed(() => {
  if (!bulkFormData.value.schoolsList) return []
  return bulkFormData.value.schoolsList
    .split('\n')
    .map(line => line.trim())
    .filter(line => line.length > 0)
})

const loadSchools = async () => {
  loading.value = true
  try {
    const response = await api.get('/schools')
    schools.value = response.data || response || []
  } catch (error) {
    console.error('Error loading schools:', error)
    alert('Failed to load schools')
    schools.value = []
  } finally {
    loading.value = false
  }
}

const loadUsers = async () => {
  try {
    const response = await api.get('/users')
    users.value = response.data || response || []
  } catch (error) {
    console.error('Error loading users:', error)
    users.value = []
  }
}

const openAddModal = () => {
  isEditing.value = false
  editingId.value = null
  formData.value = {
    name: '',
    county: '',
    poc: null
  }
  showModal.value = true
}

const openEditModal = (school: any) => {
  isEditing.value = true
  editingId.value = school.id
  formData.value = {
    name: school.name,
    county: school.county,
    poc: school.poc || null
  }
  showModal.value = true
}

const closeModal = () => {
  showModal.value = false
  isEditing.value = false
  editingId.value = null
  formData.value = {
    name: '',
    county: '',
    poc: null
  }
}

const saveSchool = async () => {
  if (!formData.value.name || !formData.value.county) {
    alert('Please fill in all required fields')
    return
  }

  submitting.value = true
  try {
    if (isEditing.value && editingId.value) {
      await api.put(`/schools/${editingId.value}`, formData.value)
    } else {
      await api.post('/schools', formData.value)
    }

    await loadSchools()
    closeModal()
  } catch (error) {
    console.error('Error saving school:', error)
    alert('Failed to save school')
  } finally {
    submitting.value = false
  }
}

const deleteSchool = async (school: any) => {
  if (!confirm(`Are you sure you want to delete ${school.name}?`)) {
    return
  }

  try {
    await api.delete(`/schools/${school.id}`)
    await loadSchools()
  } catch (error) {
    console.error('Error deleting school:', error)
    alert('Failed to delete school')
  }
}

// Bulk add functions
const openBulkAddModal = () => {
  bulkFormData.value = {
    county: '',
    schoolsList: ''
  }
  showBulkModal.value = true
}

const closeBulkModal = () => {
  showBulkModal.value = false
  bulkFormData.value = {
    county: '',
    schoolsList: ''
  }
}

const bulkCreateSchools = async () => {
  if (!bulkFormData.value.county || !bulkFormData.value.schoolsList) {
    alert('Please fill in all required fields')
    return
  }

  const schoolNames = bulkPreview.value
  if (schoolNames.length === 0) {
    alert('No valid school names found')
    return
  }

  submittingBulk.value = true
  try {
    // Create an array of school objects
    const schoolsToCreate = schoolNames.map(name => ({
      name,
      county: bulkFormData.value.county,
      poc: null
    }))

    // Send them all in parallel
    const promises = schoolsToCreate.map(school =>
      api.post('/schools', school)
    )

    await Promise.all(promises)

    await loadSchools()
    closeBulkModal()
    alert(`Successfully created ${schoolNames.length} school(s)`)
  } catch (error) {
    console.error('Error bulk creating schools:', error)
    alert('Failed to create some or all schools. Check console for details.')
  } finally {
    submittingBulk.value = false
  }
}

onMounted(() => {
  loadSchools()
  loadUsers()
})
</script>

<style scoped>
/* Add any custom styles if needed */
</style>
